import React, { useState } from "react";
import {
	Box,
	Button,
	Flex,
	Input,
	Menu,
	MenuButton,
	MenuItem,
	MenuList,
	Progress,
	Text,
	Tooltip,
	VStack
} from "@chakra-ui/react";
import { ONE, ZERO } from "../../App";
import {
	Analytics,
	getCorrectAnalytics,
	Goal,
	saveDocumentGoal
} from "../../services/documentService";

export enum GraphType {
	SESSION_TIME = "SESSION_TIME",
	UNIQUE_VISITORS = "UNIQUE_VISITORS",
	PAGES_PER_SESSION = "PAGES_PER_SESSION"
}

type TargetProps = {
	goal: Goal;
	analytics: Analytics;
};

type InputContainerProps = {
	id: string;
	setShowInput: (showInput: boolean) => void;
	targetValue: number;
	setTargetValue: (target: number) => void;
};

type ProgressContainerProps = {
	analytics: Analytics;
	graphType: GraphType;
	target: number;
};

const InputContainer = ({ id, setShowInput, targetValue, setTargetValue }: InputContainerProps) => {

	const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setTargetValue(+event.target.value);
	};

	const onClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		// You can't set a target below 0
		if (targetValue <= ZERO) return;
		saveDocumentGoal(id, targetValue).then(() => {
			setShowInput(false);
		});
	};

	return (
		<>
			<Input type="number" placeholder="Target number" onChange={onChange}/>
			<MenuItem as={Button} onClick={onClick}>Save</MenuItem>
		</>
	);
};

const ProgressContainer = ({ analytics, graphType, target }: ProgressContainerProps) => {
	const PERCENTAGE = 100;
	const getPercentage = (): number => {
		return (getCorrectAnalytics(graphType, analytics) / target) * PERCENTAGE;
	};

	return (
		<Tooltip label={
			<VStack alignItems="flex-start">
				<Text color="whiteAlpha.600" fontWeight="bold">
					{getPercentage() >= PERCENTAGE ? "Achieved" : "In Progress"}
				</Text>
				<Flex fontWeight="semibold">
					Percentage:
					<Text fontWeight="normal">
						&#160;
						{Math.round(getPercentage())}%
					</Text>
				</Flex>
				<Flex fontWeight="semibold">
					Current:
					<Text fontWeight="normal">
						{/*This adds a space*/}
						&#160;
						{getCorrectAnalytics(graphType, analytics)}
					</Text>
				</Flex>

				<Flex fontWeight="semibold">
					Target:
					<Text fontWeight="normal">
						&#160;
						{target}
					</Text>
				</Flex>
			</VStack>}>
			{/*Wrapping it in a box so the tooltip does not bug*/}
			<Box w="100%">
				<Progress value={getPercentage()} w="100%"
						  colorScheme={getPercentage() >= PERCENTAGE ? "green" : "linkedin"}/>
			</Box>
		</Tooltip>
	);
};

/**
 * Turns something like "UNIQUE_VISITORS" into "Unique Visitors"
 *
 * @param graphType Type of the graph
 * @return Split word, put space between words and capitalize the first letter of the words
 */
const getProperGraphName = (graphType: GraphType): string => {
	return graphType
		.toLowerCase()
		.split("_")
		.map(word => word.charAt(ZERO).toUpperCase() + word.slice(ONE))
		.join(" ");
};

export const Target = ({ goal: { graphType, target, documentId, id }, analytics }: TargetProps) => {
	const [showInput, setShowInput] = useState(!target);
	const [targetValue, setTargetValue] = useState(target ?? ZERO);

	return (
		<Menu autoSelect={false} closeOnSelect={false}>
			{/*This shall be changed to a '+' button*/}
			<MenuButton as={Button}>
				{getProperGraphName(graphType)}
			</MenuButton>

			<MenuList p="12px">
				<Flex direction="column" alignItems="center" w="100%" gap="12px">
					<Text
						alignSelf="start"
						fontWeight="semibold"
					>
						{getProperGraphName(graphType)}
					</Text>

					{showInput &&
						<InputContainer
							id={id} setShowInput={setShowInput}
							targetValue={targetValue}
							setTargetValue={setTargetValue}
						/>}
					{!showInput && <ProgressContainer analytics={analytics} graphType={graphType}
													  target={targetValue}/>}

				</Flex>
			</MenuList>
		</Menu>
	);
};

export default Target;